/* eslint-disable array-callback-return */
import { useEffect, useRef } from 'react';
import { colors } from '@alixpartners/ui-components';
import {
	PERMISSIONS,
	TAXONOMY_PERMISSIONS,
	TRANSACTIONS_PERMISSIONS,
} from 'contexts/PermissionsContext';

export const usePrevious = value => {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
};

export const CHART_COLORS = [
	colors.chartDarkBlue,
	colors.chartBrightTeal,
	colors.chartMediumGrey,
	colors.chartOrange,
	colors.chartDarkGreen,
	colors.chartDarkTeal,
	colors.chartLightBlue,
	colors.chartDarkGrey,
	colors.chartLightOrange,
	'#43AD80',
];

export const SPEND_BY_PROPS = {
	CATEGORY: 'category',
	COMPANY: 'company',
	VENDOR: 'harmonizedSupplier',
	RECEIVING_REGION: 'receivingRegion',
	PURCHASING_REGION: 'purchasingRegion',
	TAIL_SEGMENT: 'segmentName',
	PURCHASING_COUNTRY: 'purchasingCountry',
	BUSINESS_UNIT: 'purchasingGroup',
	PURCHASING_ENTITY: 'purchasingLegalEntity',
	CUSTOM_1: 'customfilter1',
	CUSTOM_2: 'customfilter2',
	CUSTOM_3: 'customfilter3',
};

export const getSpendByOptions = isTailSpend => {
	const options = [...SPEND_BY_OPTIONS];
	if (isTailSpend) {
		options.push({
			label: 'Tail segments',
			value: SPEND_BY_PROPS.TAIL_SEGMENT,
		});
	}
	return options;
};

export const SPEND_BY_OPTIONS = [
	{
		label: 'Categories',
		value: SPEND_BY_PROPS.CATEGORY,
	},
	{
		label: 'Companies',
		value: SPEND_BY_PROPS.COMPANY,
	},
	{
		label: 'Harmonized vendors',
		value: SPEND_BY_PROPS.VENDOR,
	},
	{
		label: 'Receiving regions',
		value: SPEND_BY_PROPS.RECEIVING_REGION,
	},
	{
		label: 'Purchasing regions',
		value: SPEND_BY_PROPS.PURCHASING_REGION,
	},
	{
		label: 'Purchasing Country',
		value: SPEND_BY_PROPS.PURCHASING_COUNTRY,
	},
	{
		label: 'Business Unit',
		value: SPEND_BY_PROPS.BUSINESS_UNIT,
	},
	{
		label: 'Purchasing Entity',
		value: SPEND_BY_PROPS.PURCHASING_ENTITY,
	},
];

export const SPEND_BY_LABELS = {
	[SPEND_BY_PROPS.CATEGORY]: 'category',
	[SPEND_BY_PROPS.COMPANY]: 'company',
	[SPEND_BY_PROPS.VENDOR]: 'harmonized vendor',
	[SPEND_BY_PROPS.RECEIVING_REGION]: 'receiving region',
	[SPEND_BY_PROPS.PURCHASING_REGION]: 'purchasing region',
	[SPEND_BY_PROPS.TAIL_SEGMENT]: 'tail segment',
	[SPEND_BY_PROPS.PURCHASING_COUNTRY]: 'purchasing country',
	[SPEND_BY_PROPS.BUSINESS_UNIT]: 'business unit',
	[SPEND_BY_PROPS.PURCHASING_ENTITY]: 'purchasing entity',
	[SPEND_BY_PROPS.CUSTOM_1]: 'custom 1',
	[SPEND_BY_PROPS.CUSTOM_2]: 'custom 2',
	[SPEND_BY_PROPS.CUSTOM_3]: 'custom 3',
};

export const SPEND_BY_LABELS_PLURAL = {
	[SPEND_BY_PROPS.CATEGORY]: 'Categories',
	[SPEND_BY_PROPS.COMPANY]: 'Companies',
	[SPEND_BY_PROPS.VENDOR]: 'Harmonized vendors',
	[SPEND_BY_PROPS.RECEIVING_REGION]: 'Receiving regions',
	[SPEND_BY_PROPS.PURCHASING_REGION]: 'Purchasing regions',
	[SPEND_BY_PROPS.TAIL_SEGMENT]: 'Tail segments',
	[SPEND_BY_PROPS.PURCHASING_COUNTRY]: 'Purchasing countries',
	[SPEND_BY_PROPS.BUSINESS_UNIT]: 'Business units',
	[SPEND_BY_PROPS.PURCHASING_ENTITY]: 'Purchasing entities',
	[SPEND_BY_PROPS.CUSTOM_1]: 'custom 1',
	[SPEND_BY_PROPS.CUSTOM_2]: 'custom 2',
	[SPEND_BY_PROPS.CUSTOM_3]: 'custom 3',
};

export const BREAKDOWN_BY = {
	NONE: '0',
	TAIL_SEGMENT: '7',
};

export const getBreakdownByOptions = (isTailSpend, customFilters) => {
	const options = [...BREAKDOWN_BY_OPTIONS];
	customFilters
		.filter(data => data.isActive)
		.filter(data => CUSTOM_FILTERS_BREAKDOWN.find(d => d === data.columnName))
		.slice(0, 3)
		.map(data => {
			options.push({
				label: data.label,
				value: (options.length + 1).toString(),
			});
		});
	if (isTailSpend) {
		options.push({
			label: 'Tail segments',
			value: BREAKDOWN_BY.TAIL_SEGMENT,
		});
	}
	return options;
};

export const BREAKDOWN_BY_OPTIONS = [
	{
		label: 'None',
		value: BREAKDOWN_BY.NONE,
	},
	{
		label: 'Companies',
		value: '1',
	},
	{
		label: 'Top L1 categories',
		value: '2',
	},
	{
		label: 'Top L4 categories',
		value: '3',
	},
	{
		label: 'Top harmonized vendors',
		value: '4',
	},
	{
		label: 'Top receiving regions',
		value: '5',
	},
	{
		label: 'Top purchasing regions',
		value: '6',
	},
	{
		label: 'Purchasing Legal Entity',
		value: '8',
	},
	{
		label: 'Business Unit',
		value: '9',
	},
	{
		label: 'Purchasing Country',
		value: '10',
	},
];
export const CUSTOM_FILTERS_BREAKDOWN = [
	'custom_data1',
	'custom_data2',
	'custom_data3',
];

export const CHART_TYPES = {
	WATERFALL: 1,
	STACKED: 2,
	STACKED100: 3,
	CLUSTERED: 4,
	BAR: 5,
};

export const CHART_TYPE_MAP = {
	[CHART_TYPES.BAR]: 'column',
	[CHART_TYPES.CLUSTERED]: 'column',
	[CHART_TYPES.STACKED]: 'column',
	[CHART_TYPES.STACKED100]: 'column',
	[CHART_TYPES.WATERFALL]: 'waterfall',
};

export const CHART_TYPE_OPTIONS_WITHOUT_BREAKDOWN = [
	{
		label: 'Waterfall chart',
		value: CHART_TYPES.WATERFALL,
	},
	{
		label: 'Bar chart',
		value: CHART_TYPES.BAR,
	},
];

export const CHART_TYPE_OPTIONS = [
	{
		label: 'Waterfall chart',
		value: CHART_TYPES.WATERFALL,
	},
	{
		label: 'Stacked bar chart',
		value: CHART_TYPES.STACKED,
	},
	{
		label: '100% stacked bar chart',
		value: CHART_TYPES.STACKED100,
	},
];

export const SPEND_TYPE = {
	INDIRECT: 1,
	DIRECT: 0,
};

export const SPEND_TYPE_VALUE = {
	DIRECT: 'direct',
	INDIRECT: 'indirect',
};

export const CATEGORY_TYPE = {
	AP: 'apcategory',
	CLIENT: 'clientCategory',
	DEFAULT: 'category',
};

export const TAXONOMY_TYPES = {
	AP: 3,
	CLIENT: 2,
	PROJECT: 1,
};

export const EXPORT_OPTIONS = [
	{
		label: 'Export as image',
		value: 'png',
	},
	{
		label: 'Export as data',
		value: 'xlsx',
	},
];

export const NAV_CONFIG = [
	{
		path: '/spend',
		name: 'spend',
		label: 'Investigate',
		requiredSome: [PERMISSIONS.AnalysisRead],
	},
	{
		path: '/taxonomymapper',
		name: 'taxonomy-mapper',
		label: 'Taxonomy Mapper',
		requiredSome: TAXONOMY_PERMISSIONS,
	},
	{
		path: '/spend/explore',
		name: 'transactions',
		label: 'Transactions',
		requiredSome: TRANSACTIONS_PERMISSIONS,
	},
	{
		path: '/spend/pivot-table',
		name: 'pivot-table',
		label: 'Slice & Dice',
		requiredSome: [PERMISSIONS.AnalysisRead],
	},
];

export const SPEND_TYPE_LABELS = ['Direct', 'Indirect'];
export const UNKNOWN = 'UNKNOWN';
export const makeEmptyFromUnknown = val => (val === UNKNOWN ? '' : val);
export const makeUnknownFromEmpty = val => (val === '' ? UNKNOWN : val);

export const convertToArray = data =>
	data ? (Array.isArray(data) ? data : [data]) : [];

export const getCurrencySymbolFromOptions = (options, currency) => {
	if (!options) {
		return currency ? currency.toUpperCase() : '';
	}

	if (!currency) {
		return '';
	}

	const currencyObj =
		options.find(item => item.code === currency.toLowerCase()) || {};

	return currencyObj.symbol || currency.toUpperCase();
};

export const CATEGORY_BY_TAXONOMY = {
	[TAXONOMY_TYPES.PROJECT]: CATEGORY_TYPE.DEFAULT,
	[TAXONOMY_TYPES.CLIENT]: CATEGORY_TYPE.CLIENT,
	[TAXONOMY_TYPES.AP]: CATEGORY_TYPE.AP,
};

export const getCategoryParam = ({
	spendType,
	indirectTaxonomy,
	directTaxonomy,
}) => {
	if (spendType == null) {
		return CATEGORY_TYPE.DEFAULT;
	}
	const taxonomy =
		spendType === SPEND_TYPE.DIRECT ? directTaxonomy : indirectTaxonomy;

	return CATEGORY_BY_TAXONOMY[taxonomy] || CATEGORY_TYPE.DEFAULT;
};
